<template>
	<div
		ref="stickyBarRef"
		class="fixed border-y border-dj-gray-light sm:top-0 right-0 left-0 bottom-0 sm:bottom-auto w-full h-max drop-shadow-[0_-6px_3px_rgba(0,0,0,0.1)] sm:drop-shadow-md sm:h-dj-80 sm:px-2 bg-white z-40"
	>
		<div class="flex flex-col sm:flex-row sm:items-center p-2 sm:p-0 h-full max-w-[1400px] mx-auto">
			<BaseCloudImage
				v-if="jobData?.company?.logo?.cloudinary"
				class="hidden sm:block cursor-pointer"
				custom-class="rounded-normal overflow-hidden"
				:public-id="jobData.company.logo.cloudinary"
				:transformation="[{ width: 40, height: 40, crop: 'scale' }]"
				alt="company-logo"
				@click="navigateToCompany"
			/>
			<span class="sm:ml-6 mb-1 sm:mb-0 font-medium text-sm sm:text-base">
				{{ jobData?.name?.[locale] || "Job name" }}
			</span>
			<div class="ml-0 sm:ml-auto sm:hidden md:block mb-1 sm:mb-0">
				<div
					v-if="jobData?.jobSalary?.notSpecified === false && jobData?.jobSalary?.salary"
					class="flex flex-col"
				>
					<span class="text-sm text-dj-gray mb-1">
						{{ $t(`job.${jobData.jobSalary.amountIn}`) }}
						<template v-if="jobData.jobSalary.salaryPeriod === 'hour'">{{ $t("job.perHour") }}</template>
						<template v-else-if="jobData.jobSalary.salaryPeriod === 'month'">
							{{ $t("job.perMonth") }}
						</template>
					</span>
					<span>{{ jobData.jobSalary.salary }} {{ jobData.jobSalary.currency.toLocaleUpperCase() }}</span>
				</div>
			</div>
			<div class="flex ml-0 sm:ml-auto">
				<div
					@click="saveJob"
					:class="[
						'flex items-center justify-center cursor-pointer ml-auto',
						jobData?.expiredJob ? 'text-dj-gray' : 'text-dj-blue',
					]"
				>
					<BaseIcon
						class="mr-[10px] text-xl"
						:icon="savedJob ? 'ic:outline-favorite' : 'ic:outline-favorite-border'"
						color="currentColor"
					/>
					<span class="text-sm">{{ savedJob ? $t("job.card.deleteJob") : $t("job.card.saveJob") }}</span>
				</div>
				<template v-if="alreadyApplied">
					<NuxtLink :to="`/${$i18n.locale}/my-applications`" class="ml-9 text-sm text-dj-red">
						{{ $t("job.card.alreadyApplied") }}
					</NuxtLink>
				</template>
				<template v-else>
					<BaseButton
						@click="$emit('onApply')"
						:disabled="jobData?.expiredJob"
						class="ml-9"
						type="primary-red"
					>
						{{ $t("job.card.jobApplication") }}
					</BaseButton>
				</template>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted, PropType } from "vue";
import { capitalizeFirstLetter } from "~~/utils";
import { type JobItem, getJobSlug } from "@@/composables/useJob";

export default defineComponent({
	name: "JobProfileStickyBar",
	props: {
		jobData: {
			type: Object as PropType<JobItem>,
			default: null,
		},
		alreadyApplied: {
			type: Boolean,
			required: true,
		},
		savedJob: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["onApply", "onSavedChange"],
	setup(props, { emit }) {
		const app = useNuxtApp();
		const router = useRouter();
		const route = useRoute();
		const isAuth = useState("isAuth");
		const { locale } = useI18n();
		const { t } = useI18n();

		const stickyBarRef = ref<HTMLElement | null>(null);
		const stickyBarHeight = useState("stickyBarHeight", () => 0);
		let resizeObserver: ResizeObserver | null = null;

		onMounted(() => {
			if (stickyBarRef.value) {
				resizeObserver = new ResizeObserver((entries) => {
					stickyBarHeight.value = entries[0].contentRect.height;
				});
				resizeObserver.observe(stickyBarRef.value);
			}
		});

		onUnmounted(() => {
			resizeObserver?.disconnect();
			stickyBarHeight.value = 0;
		});

		function saveJob() {
			if (!props.jobData?.expiredJob) {
				if (isAuth.value) {
					if (props.savedJob) {
						// Remove job
						$fetch("/api/save-job", {
							method: "POST",
							body: {
								token: useCookie("token").value,
								xsrf: useCookie("XSRF-TOKEN").value,
								locale: locale.value,
								jobSlug: getJobSlug(props.jobData, locale.value),
								companySlug: props.jobData?.company?.slug,
								operation: "remove-saved-job-from-user",
							},
						}).then(() => {
							app.$openToast({
								message: t("job.alert.successfulRemove"),
								description: t("job.alert.successfulRemoveDesc"),
								type: "success",
							});

							useAuth.updateUser();

							emit("onSavedChange", false);
						});
					} else {
						// Save job
						$fetch("/api/save-job", {
							method: "POST",
							body: {
								token: useCookie("token").value,
								xsrf: useCookie("XSRF-TOKEN").value,
								locale: locale.value,
								jobSlug: getJobSlug(props.jobData, locale.value),
								companySlug: props.jobData?.company?.slug,
								operation: "save-job-to-user",
							},
						}).then(() => {
							app.$openToast({
								message: t("job.alert.successfulSave"),
								description: t("job.alert.successfulSaveDesc"),
								type: "success",
							});

							useAuth.updateUser();

							emit("onSavedChange", true);
						});
					}
				} else {
					router.push({
						path: `/${locale.value}/login`,
						query: {
							r: route.fullPath,
						},
					});
				}
			}
		}

		function navigateToCompany() {
			router.push({
				path: `/${locale.value}/company/${props.jobData?.company?.slug}`,
			});
		}

		return {
			locale,
			saveJob,
			navigateToCompany,
			capitalizeFirstLetter,
			stickyBarRef,
		};
	},
});
</script>
